import type { InputHTMLAttributes, MouseEvent, ReactNode } from 'react';

import { type SwitchSizeType } from './types';
import { cn } from '../../utils/cn';
import type { EventBehaviorProps } from '../../utils/EventBehavior';
import { handleEventBehavior } from '../../utils/EventBehavior';

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>, EventBehaviorProps {
    label?: ReactNode;
    labelClassName?: string;
    id: string;
    size?: SwitchSizeType;
}

const switchSizeClassNames: Record<SwitchSizeType, { label: string; thumb: string; track: string }> = {
    default: {
        label: 'text-sm',
        thumb: 'size-5 shadow-lg',
        track: 'h-6 w-11 [&>span]:translate-x-0 peer-checked:[&>span]:translate-x-5',
    },
    sm: {
        label: 'text-xs',
        thumb: 'size-3 shadow-md',
        track: 'h-4 w-7 [&>span]:translate-x-0 peer-checked:[&>span]:translate-x-3',
    },
};

/**
 * A styled switch/toggle component with optional label support.
 *
 * @param id - **Required.** Unique identifier for the switch input.
 * @param label - Optional label displayed next to the switch.
 * @param labelClassName - Additional CSS classes for the label.
 * @param size - Visual size (`default` or `sm`). Defaults to `default`.
 * @param className - Additional CSS classes to merge with default styles.
 * @param disabled - Whether the switch is disabled.
 * @param checked - Controlled checked state.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * <Switch id="notifications" />
 *
 * @example
 * <Switch id="darkMode" label="Enable dark mode" />
 *
 * @example
 * <Switch id="feature" checked={isEnabled} onChange={handleChange} />
 *
 * @example
 * <Switch id="locked" disabled label="Premium feature" />
 *
 * @example
 * <Switch id="compact" label="Compact" size="sm" />
 */
export const Switch: React.FC<SwitchProps> = ({
    className,
    disabled,
    eventPreventDefault,
    eventStopPropagation,
    id,
    label,
    labelClassName,
    onClick,
    size = 'default',
    ...props
}) => {
    const labelId = `${id}-label`;
    const sizeClasses = switchSizeClassNames[size];

    return (
        <label
            className={cn(
                'group inline-flex cursor-pointer items-center select-none',
                size === 'sm' ? 'gap-1.5' : 'gap-2',
                disabled && 'cursor-not-allowed',
            )}
            htmlFor={id}
        >
            <span className="relative inline-flex">
                <input
                    type="checkbox"
                    id={id}
                    role="switch"
                    className="peer sr-only"
                    disabled={disabled}
                    aria-labelledby={label ? labelId : undefined}
                    aria-checked={props.checked}
                    onClick={(e: MouseEvent<HTMLInputElement>) => {
                        handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
                        onClick?.(e);
                    }}
                    {...props}
                />

                <span
                    className={cn(
                        'flex items-center rounded-full px-0.5 transition-colors',
                        sizeClasses.track,
                        'bg-grey-outline-light peer-checked:bg-midnight',
                        'peer-focus-visible:ring-2 peer-focus-visible:ring-blue-focus peer-focus-visible:ring-offset-1',
                        'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
                        className,
                    )}
                >
                    <span className={cn('rounded-full bg-white transition-transform', sizeClasses.thumb)} />
                </span>
            </span>

            {label && (
                <span
                    id={labelId}
                    className={cn(
                        'text-midnight group-has-[:disabled]:text-midnight-disabled-text',
                        sizeClasses.label,
                        labelClassName,
                    )}
                >
                    {label}
                </span>
            )}
        </label>
    );
};
